import type { Alphabet } from './alphabet';
import {
  type CellIndex,
  type Dir,
  GRID,
  cellToIndex,
  chebyshev,
  indexToX,
  indexToY,
  manhattan,
  stepIndex,
} from './grid';
import type { Question } from './pack';
import { type RngCursor, nextInt, takeK } from './rng';
import { ANSWER_PAIR_MIN_DIST, type EngineRules } from './rules';
import type { EngineEvent, Tile } from './types';

export interface SpawnInput {
  readonly head: CellIndex;
  readonly dir: Dir;
  readonly body: readonly CellIndex[];
  readonly question: Question;
  readonly rules: EngineRules;
  readonly alphabet: Alphabet;
}

export interface SpawnOutput {
  readonly tiles: readonly Tile[];
  readonly events: readonly EngineEvent[];
}

/** 스폰 가능 영역 — 여백을 뺀 직사각형. */
export function spawnRegion(rules: EngineRules): CellIndex[] {
  const m = rules.spawnMargin;
  const out: CellIndex[] = [];
  for (let y = m; y < GRID.ROWS - m; y++) {
    for (let x = m; x < GRID.COLS - m; x++) out.push(cellToIndex(x, y));
  }
  return out;
}

/** 머리 칸 + 진행 방향 앞 spawnSafeAhead 칸. */
function safeAheadCells(head: CellIndex, dir: Dir, ahead: number): Set<CellIndex> {
  const s = new Set<CellIndex>([head]);
  let i = head;
  for (let n = 0; n < ahead; n++) {
    i = stepIndex(i, dir);
    if (i < 0) break;
    s.add(i);
  }
  return s;
}

function quadrant(c: CellIndex): number {
  const qx = indexToX(c) < GRID.COLS / 2 ? 0 : 1;
  const qy = indexToY(c) < GRID.ROWS / 2 ? 0 : 1;
  return qy * 2 + qx;
}

/** a→b 최단 경로 바운딩 사각형 안에 c 가 있는가. */
function inBox(a: CellIndex, b: CellIndex, c: CellIndex): boolean {
  const ax = indexToX(a);
  const ay = indexToY(a);
  const bx = indexToX(b);
  const by = indexToY(b);
  const cx = indexToX(c);
  const cy = indexToY(c);
  return (
    cx >= Math.min(ax, bx) &&
    cx <= Math.max(ax, bx) &&
    cy >= Math.min(ay, by) &&
    cy <= Math.max(ay, by)
  );
}

function gapOk(c: CellIndex, taken: readonly CellIndex[], minGap: number): boolean {
  for (const t of taken) if (chebyshev(c, t) < minGap) return false;
  return true;
}

/** 사다리 창에서 오답 글자를 무복원 추출한다. */
export function pickLadderChars(
  decoy: RngCursor,
  question: Question,
  need: number,
  rules: EngineRules,
  alphabet: Alphabet,
): { chars: string[]; degraded: boolean } {
  const offset = nextInt(decoy, question.a.length);
  const ladder = alphabet.ladder(question.a, question.d, offset);
  const windowLen = Math.min(need * rules.ladderWindowFactor, ladder.length);
  const win = ladder.slice(0, windowLen);
  const take = Math.min(need, win.length);
  return { chars: takeK(decoy, win, take), degraded: take < need };
}

interface PlaceCtx {
  readonly region: readonly CellIndex[];
  readonly blocked: Set<CellIndex>;
  readonly rules: EngineRules;
  readonly head: CellIndex;
  readonly len: number;
}

function answerCandidates(
  ctx: PlaceCtx,
  k: number,
  level: number,
  bandCenter: number,
  cand: readonly CellIndex[],
  placed: readonly CellIndex[],
): CellIndex[] {
  const r = ctx.rules;
  const pairMin = level >= 3 ? 4 : (ANSWER_PAIR_MIN_DIST[ctx.len] as number);
  const lo = Math.max(r.tileHeadMinDist, bandCenter - r.answerBandSpread);
  const hi = Math.min(r.tileHeadMaxDist, bandCenter + r.answerBandSpread);
  const quadCap = Math.ceil(ctx.len / 2);
  const out: CellIndex[] = [];

  for (const c of ctx.region) {
    if (ctx.blocked.has(c)) continue;
    if (!gapOk(c, placed, r.minTileGap)) continue;
    if (!gapOk(c, cand, r.minTileGap)) continue;

    const dHead = manhattan(ctx.head, c);
    if (dHead < lo || dHead > hi) continue;

    let ok = true;
    for (const other of cand) {
      if (manhattan(c, other) < pairMin) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;

    if (level < 2) {
      let n = 1;
      const q = quadrant(c);
      for (const other of cand) if (quadrant(other) === q) n++;
      if (n > quadCap) continue;
    }

    if (level < 1 && k > 0) {
      let from = ctx.head;
      let hit = false;
      for (let i = 0; i < k; i++) {
        const to = cand[i] as CellIndex;
        if (inBox(from, to, c)) {
          hit = true;
          break;
        }
        from = to;
      }
      if (hit) continue;
    }

    out.push(c);
  }
  return out;
}

function placeAnswerTiles(
  layout: RngCursor,
  ctx: PlaceCtx,
  events: EngineEvent[],
): CellIndex[] | null {
  const r = ctx.rules;
  const span = r.answerBandCenterMax - r.answerBandCenterMin + 1;
  const bandCenter = r.answerBandCenterMin + nextInt(layout, span);
  const levels: number[] = new Array(ctx.len).fill(0);

  for (let round = 0; round <= 3; round++) {
    let lastBlocked = 0;
    for (let attempt = 0; attempt < r.spawnAttemptsPerLevel; attempt++) {
      const cand: CellIndex[] = [];
      let blocked = -1;
      for (let k = 0; k < ctx.len; k++) {
        const pool = answerCandidates(ctx, k, levels[k] as number, bandCenter, cand, []);
        if (pool.length === 0) {
          blocked = k;
          break;
        }
        cand.push(pool[nextInt(layout, pool.length)] as CellIndex);
      }
      if (blocked < 0) return cand;
      lastBlocked = blocked;
    }
    if (round === 3) break;
    const lv = Math.min(3, (levels[lastBlocked] as number) + 1);
    levels[lastBlocked] = lv;
    events.push({ kind: 'spawn_relaxed', slot: lastBlocked, level: lv as 0 | 1 | 2 | 3 });
  }
  return null;
}

/** 제약을 최소한(영역 + 간격)만 지키는 비상 배치. */
function degradedPlacement(
  rng: RngCursor,
  ctx: PlaceCtx,
  count: number,
  placed: readonly CellIndex[],
): CellIndex[] {
  const out: CellIndex[] = [];
  for (let n = 0; n < count; n++) {
    const pool: CellIndex[] = [];
    for (const c of ctx.region) {
      if (ctx.blocked.has(c)) continue;
      if (!gapOk(c, placed, ctx.rules.minTileGap)) continue;
      if (!gapOk(c, out, ctx.rules.minTileGap)) continue;
      pool.push(c);
    }
    if (pool.length === 0) break;
    out.push(pool[nextInt(rng, pool.length)] as CellIndex);
  }
  return out;
}

function placeDistractors(
  decoy: RngCursor,
  ctx: PlaceCtx,
  answerCells: readonly CellIndex[],
  chars: readonly string[],
  events: EngineEvent[],
): { cells: CellIndex[]; used: number } {
  const r = ctx.rules;
  const placed: CellIndex[] = [...answerCells];
  const out: CellIndex[] = [];

  // 머리 거리 제약은 정답·오답 모두에 적용된다(SPEC §7.1).
  // 오답에 빼먹으면 스폰 직후 피할 수 없는 오답이 생겨 지식과 무관한 페널티가 된다.
  const headOk = (c: CellIndex): boolean => {
    const d = manhattan(ctx.head, c);
    return d >= r.tileHeadMinDist && d <= r.tileHeadMaxDist;
  };

  // 1) 근접 함정 — 정답 타일 근처 1장씩
  for (let k = 0; k < answerCells.length && out.length < chars.length; k++) {
    const anchor = answerCells[k] as CellIndex;
    const pool: CellIndex[] = [];
    for (const c of ctx.region) {
      if (ctx.blocked.has(c)) continue;
      if (!headOk(c)) continue;
      const d = manhattan(anchor, c);
      if (d < r.decoyMinDist || d > r.decoyMaxDist) continue;
      if (!gapOk(c, placed, r.minTileGap)) continue;
      pool.push(c);
    }
    if (pool.length === 0) continue;
    const pick = pool[nextInt(decoy, pool.length)] as CellIndex;
    out.push(pick);
    placed.push(pick);
  }

  // 2) 나머지는 영역 전체에 산개 (머리 거리 제약은 유지한다)
  while (out.length < chars.length) {
    const pool: CellIndex[] = [];
    for (const c of ctx.region) {
      if (ctx.blocked.has(c)) continue;
      if (!headOk(c)) continue;
      if (!gapOk(c, placed, r.minTileGap)) continue;
      pool.push(c);
    }
    if (pool.length === 0) {
      events.push({ kind: 'spawn_degraded', wantTiles: chars.length, gotTiles: out.length });
      break;
    }
    const pick = pool[nextInt(decoy, pool.length)] as CellIndex;
    out.push(pick);
    placed.push(pick);
  }
  return { cells: out, used: out.length };
}

/** 한 문제의 타일 전체를 놓는다. layout → decoy 순서로만 스트림을 소비한다. */
export function spawnTiles(input: SpawnInput, layout: RngCursor, decoy: RngCursor): SpawnOutput {
  const { head, dir, body, question, rules, alphabet } = input;
  const events: EngineEvent[] = [];
  const len = question.a.length;

  const blocked = new Set<CellIndex>(body);
  for (const c of safeAheadCells(head, dir, rules.spawnSafeAhead)) blocked.add(c);

  const ctx: PlaceCtx = { region: spawnRegion(rules), blocked, rules, head, len };

  let answerCells = placeAnswerTiles(layout, ctx, events);
  if (answerCells === null) {
    answerCells = degradedPlacement(layout, ctx, len, []);
    events.push({ kind: 'spawn_degraded', wantTiles: len, gotTiles: answerCells.length });
  }

  const want = rules.decoyOn ? rules.distractorCount : 0;
  const picked =
    want > 0
      ? pickLadderChars(decoy, question, want, rules, alphabet)
      : { chars: [], degraded: false };
  if (picked.degraded) {
    events.push({ kind: 'spawn_degraded', wantTiles: want, gotTiles: picked.chars.length });
  }
  const dec = placeDistractors(decoy, ctx, answerCells, picked.chars, events);

  const tiles: Tile[] = [];
  for (let k = 0; k < answerCells.length; k++) {
    tiles.push({ cell: answerCells[k] as CellIndex, ch: question.a.charAt(k), kind: 0, slot: k });
  }
  for (let k = 0; k < dec.used; k++) {
    tiles.push({
      cell: dec.cells[k] as CellIndex,
      ch: picked.chars[k] as string,
      kind: 1,
      slot: -1,
    });
  }

  const uniq = new Set(tiles.map((t) => t.cell));
  if (uniq.size !== tiles.length) throw new Error('spawnTiles: 좌표 중복');

  return { tiles, events };
}

/** 재배치 — 글자는 유지하고 좌표만 respawn 스트림으로 다시 뽑는다(scope='all'). */
export function relocateTiles(
  tiles: readonly Tile[],
  input: SpawnInput,
  respawn: RngCursor,
): SpawnOutput {
  const { head, dir, body, rules } = input;
  const blocked = new Set<CellIndex>(body);
  for (const c of safeAheadCells(head, dir, rules.spawnSafeAhead)) blocked.add(c);
  const answers = tiles.filter((t) => t.kind === 0);
  const ctx: PlaceCtx = { region: spawnRegion(rules), blocked, rules, head, len: answers.length };

  const events: EngineEvent[] = [];
  let answerCells = ctx.len > 0 ? placeAnswerTiles(respawn, ctx, events) : [];
  if (answerCells === null) answerCells = degradedPlacement(respawn, ctx, ctx.len, []);

  const decoys = tiles.filter((t) => t.kind === 1);
  const dec = placeDistractors(
    respawn,
    ctx,
    answerCells,
    decoys.map((t) => t.ch),
    events,
  );

  const out: Tile[] = [];
  for (let k = 0; k < answerCells.length && k < answers.length; k++) {
    const a = answers[k] as Tile;
    out.push({ cell: answerCells[k] as CellIndex, ch: a.ch, kind: 0, slot: a.slot });
  }
  for (let k = 0; k < dec.used && k < decoys.length; k++) {
    out.push({ cell: dec.cells[k] as CellIndex, ch: (decoys[k] as Tile).ch, kind: 1, slot: -1 });
  }
  // 재배치가 낸 spawn_degraded/relaxed 도 버리지 않고 돌려준다 — 계측이 길이 대역별 열화를 감시한다
  return { tiles: out, events };
}

export function tileAt(tiles: readonly Tile[], cell: CellIndex): Tile | null {
  for (const t of tiles) if (t.cell === cell) return t;
  return null;
}

export function nextAnswerTile(tiles: readonly Tile[], eaten: number): Tile | null {
  for (const t of tiles) if (t.kind === 0 && t.slot === eaten) return t;
  return null;
}
